import { useCallback, useEffect, useMemo, useState } from "react";

import { UsersApiHelperForMember } from "api/ApiHelper";
import dayjs from "dayjs";

// Maximum data points to display on chart
const AXIS_DATES_MAX = 50;

/**
 * Custom hook to fetch and aggregate debt service payment data for charting
 * @param {string} debt_schedule_id - The debt schedule ID
 * @param {Object} globalState - Global state object containing producer_token
 * @returns {Object} Hook return object with chart data and loading state
 */
export default function useDebtScheduleAmortizationDebtServiceChart(debt_schedule_id, globalState) {
  const [amortizationSchedule, setAmortizationSchedule] = useState([]);
  const [debtSchedule, setDebtSchedule] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const api = UsersApiHelperForMember;

  /**
   * Load debt schedule to get payment_frequency, inception_date, maturity_date
   */
  const loadDebtSchedule = useCallback(async () => {
    if (!debt_schedule_id || String(debt_schedule_id) === "0") {
      setDebtSchedule(null);
      return;
    }

    try {
      const response = await api.callPost(
        "debtSchedule/Search",
        {
          id: debt_schedule_id,
          producer_token: globalState?.producer_token,
          is_active: true,
        },
        globalState
      );
      if (response && response.length > 0) {
        setDebtSchedule(response[0]);
      }
    } catch (error) {
      console.error("Error loading debt schedule", error);
    }
  }, [debt_schedule_id, globalState?.producer_token]);

  /**
   * Load amortization schedule from API
   */
  const loadSchedule = useCallback(async () => {
    // Don't load if debt_schedule_id is not valid
    if (!debt_schedule_id || String(debt_schedule_id) === "0") {
      setAmortizationSchedule([]);
      return;
    }

    try {
      setIsLoading(true);
      const payload = {
        producer_token: globalState.producer_token,
        debt_schedule_id: debt_schedule_id,
        is_active: true,
      };

      const response = await api.callPost("DebtScheduleAmortization/search", payload, globalState);
      if (response && Array.isArray(response)) {
        // Sort by payment_number ascending
        const sortedResponse = [...response].sort((a, b) => a.payment_number - b.payment_number);
        setAmortizationSchedule(sortedResponse);
      } else {
        setAmortizationSchedule([]);
      }
    } catch (error) {
      console.error("Error loading amortization schedule", error);
      setAmortizationSchedule([]);
    } finally {
      setIsLoading(false);
    }
  }, [debt_schedule_id, globalState?.producer_token]);

  /**
   * Load debt schedule and amortization schedule on mount and when dependencies change
   */
  useEffect(() => {
    if (globalState?.producer_token) {
      loadDebtSchedule();
      loadSchedule();
    }
  }, [loadDebtSchedule, loadSchedule, globalState?.producer_token]);

  /**
   * Determine aggregation level based on payment count and frequency
   */
  const getAggregationLevel = useCallback((totalPayments, paymentFrequency) => {
    // If more than max allowed, always aggregate by year
    if (totalPayments > AXIS_DATES_MAX) {
      return "year";
    }

    // Otherwise, respect the payment frequency
    const frequency = paymentFrequency?.toLowerCase() || "";

    if (frequency.includes("annual") || frequency.includes("yearly")) {
      return "as-scheduled"; // Annual payments
    } else if (frequency.includes("semi-annual")) {
      return "as-scheduled"; // Semi-annual payments
    } else if (frequency.includes("quarterly")) {
      return totalPayments <= AXIS_DATES_MAX ? "as-scheduled" : "year";
    } else if (frequency.includes("monthly")) {
      return totalPayments <= AXIS_DATES_MAX ? "as-scheduled" : "year";
    } else if (frequency.includes("bi-weekly") || frequency.includes("weekly")) {
      // Weekly/Bi-weekly always needs aggregation
      if (totalPayments <= AXIS_DATES_MAX) {
        return "month";
      } else {
        return "year";
      }
    }

    // Default: show as-is if under limit, otherwise aggregate by year
    return totalPayments <= AXIS_DATES_MAX ? "as-scheduled" : "year";
  }, []);

  /**
   * Aggregate payments by time period - returns stacked bar format
   */
  const aggregatePayments = useCallback((payments, aggregationLevel) => {
    if (aggregationLevel === "as-scheduled") {
      // No aggregation needed - return raw data in stacked bar format
      const result = [];
      payments.forEach((payment, idx) => {
        const dateKey = payment.payment_date;
        const stack = idx + 1;

        result.push(
          {
            payment_date: dateKey,
            category: "Principal Balance",
            dataValueY_mean: parseFloat(payment.beginning_balance || 0),
            stack: stack,
            payment_number: payment.payment_number,
          },
          {
            payment_date: dateKey,
            category: "Principal",
            dataValueY_mean: parseFloat(payment.principal_payment || 0),
            stack: stack,
            payment_number: payment.payment_number,
          },
          {
            payment_date: dateKey,
            category: "Interest",
            dataValueY_mean: parseFloat(payment.interest_payment || 0),
            stack: stack,
            payment_number: payment.payment_number,
          },
          {
            payment_date: dateKey,
            category: "Extra Principal",
            dataValueY_mean: parseFloat(payment.extra_principal_payment || 0),
            stack: stack,
            payment_number: payment.payment_number,
          }
        );
      });
      return result;
    }

    // Group payments by aggregation period
    const grouped = {};

    payments.forEach((payment) => {
      const paymentDate = dayjs(payment.payment_date);
      let key;
      let representativeDate;

      if (aggregationLevel === "year") {
        key = paymentDate.format("YYYY");
        representativeDate = `${key}-01-01`;
      } else if (aggregationLevel === "month") {
        key = paymentDate.format("YYYY-MM");
        representativeDate = `${key}-01`;
      } else if (aggregationLevel === "quarter") {
        const quarter = Math.floor(paymentDate.month() / 3) + 1;
        const year = paymentDate.year();
        key = `${year}-Q${quarter}`;
        // First month of quarter
        const quarterMonth = (quarter - 1) * 3 + 1;
        representativeDate = `${year}-${String(quarterMonth).padStart(2, "0")}-01`;
      }

      if (!grouped[key]) {
        grouped[key] = {
          payment_date: representativeDate,
          principal_balance: 0,
          principal_total: 0,
          interest_total: 0,
          extra_principal_total: 0,
          period_label: key,
          count: 0,
        };
      }

      // For principal balance, take the beginning balance (last payment in the period)
      grouped[key].principal_balance = parseFloat(payment.beginning_balance || 0);
      grouped[key].principal_total += parseFloat(payment.principal_payment || 0);
      grouped[key].interest_total += parseFloat(payment.interest_payment || 0);
      grouped[key].extra_principal_total += parseFloat(payment.extra_principal_payment || 0);
      grouped[key].count += 1;
    });

    // Convert to array, sort by date, and transform to stacked bar format
    const sortedGroups = Object.values(grouped).sort((a, b) => {
      return dayjs(a.payment_date).isBefore(dayjs(b.payment_date)) ? -1 : 1;
    });

    const result = [];
    sortedGroups.forEach((group, idx) => {
      const stack = idx + 1;
      result.push(
        {
          payment_date: group.payment_date,
          category: "Principal Balance",
          dataValueY_mean: group.principal_balance,
          stack: stack,
          period_label: group.period_label,
        },
        {
          payment_date: group.payment_date,
          category: "Principal",
          dataValueY_mean: group.principal_total,
          stack: stack,
          period_label: group.period_label,
        },
        {
          payment_date: group.payment_date,
          category: "Interest",
          dataValueY_mean: group.interest_total,
          stack: stack,
          period_label: group.period_label,
        },
        {
          payment_date: group.payment_date,
          category: "Extra Principal",
          dataValueY_mean: group.extra_principal_total,
          stack: stack,
          period_label: group.period_label,
        }
      );
    });

    return result;
  }, []);

  /**
   * Calculate chart data with appropriate aggregation
   */
  const chartData = useMemo(() => {
    if (!amortizationSchedule || amortizationSchedule.length === 0) {
      return [];
    }

    const totalPayments = amortizationSchedule.length;
    const paymentFrequency = debtSchedule?.payment_frequency || "";

    const aggregationLevel = getAggregationLevel(totalPayments, paymentFrequency);
    const aggregatedData = aggregatePayments(amortizationSchedule, aggregationLevel);

    return aggregatedData;
  }, [amortizationSchedule, debtSchedule, getAggregationLevel, aggregatePayments]);

  /**
   * Get aggregation level info for display
   */
  const aggregationLevel = useMemo(() => {
    if (!amortizationSchedule || amortizationSchedule.length === 0) {
      return "as-scheduled";
    }

    const totalPayments = amortizationSchedule.length;
    const paymentFrequency = debtSchedule?.payment_frequency || "";

    return getAggregationLevel(totalPayments, paymentFrequency);
  }, [amortizationSchedule, debtSchedule, getAggregationLevel]);

  return {
    chartData,
    isLoading,
    aggregationLevel,
  };
}
